import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";
import { Button } from "../ui/button.jsx";
import { ScrollArea } from "../ui/scroll-area.jsx";
import { Database, Copy, Check, Download } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs.jsx";

export default function MySQLSchema() {
  const [copied, setCopied] = useState(false);
  
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(sqlScript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([sqlScript], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = 'helpdesk_pro_schema.sql';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };
  
  const sqlScript = `-- MySQL Database Script for HelpDesk Pro
-- This script creates all necessary tables and relationships for the helpdesk system

SET NAMES utf8mb4;
SET FOREIGN_KEY_CHECKS = 0;

-- -----------------------------------------------------
-- Schema helpdesk_pro
-- -----------------------------------------------------
CREATE SCHEMA IF NOT EXISTS \`helpdesk_pro\` DEFAULT CHARACTER SET utf8mb4 COLLATE utf8mb4_unicode_ci;
USE \`helpdesk_pro\`;

-- -----------------------------------------------------
-- Table \`users\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`users\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`email\` VARCHAR(255) NOT NULL,
  \`full_name\` VARCHAR(255) NOT NULL,
  \`role\` ENUM('admin', 'manager', 'agent', 'client') NOT NULL DEFAULT 'client',
  \`password_hash\` VARCHAR(255) NULL,
  \`company_id\` VARCHAR(36) NULL,
  \`avatar_url\` VARCHAR(255) NULL,
  \`phone\` VARCHAR(50) NULL,
  \`position\` VARCHAR(100) NULL,
  \`department\` VARCHAR(100) NULL,
  \`status\` ENUM('active', 'inactive', 'pending') NOT NULL DEFAULT 'active',
  \`last_login\` DATETIME NULL,
  \`preferences\` JSON NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  UNIQUE INDEX \`email_UNIQUE\` (\`email\` ASC),
  INDEX \`company_id_idx\` (\`company_id\` ASC)
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`companies\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`companies\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`corporate_name\` VARCHAR(255) NOT NULL,
  \`trade_name\` VARCHAR(255) NULL,
  \`cnpj\` VARCHAR(20) NOT NULL,
  \`status\` ENUM('active', 'inactive') NOT NULL DEFAULT 'active',
  \`address\` JSON NULL COMMENT 'Street, number, complement, district, city, state, zip code',
  \`plan\` JSON NULL COMMENT 'Plan details including name, price, billing_day, features',
  \`financial\` JSON NULL COMMENT 'Payment method, billing address, bank info',
  \`sla_config\` JSON NULL COMMENT 'SLA configuration for different priorities',
  \`created_by\` VARCHAR(36) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  UNIQUE INDEX \`cnpj_UNIQUE\` (\`cnpj\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_companies_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- Add foreign key from users to companies
ALTER TABLE \`users\` 
ADD CONSTRAINT \`fk_users_company_id\`
  FOREIGN KEY (\`company_id\`)
  REFERENCES \`companies\` (\`id\`)
  ON DELETE SET NULL
  ON UPDATE CASCADE;

-- -----------------------------------------------------
-- Table \`company_contacts\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`company_contacts\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`company_id\` VARCHAR(36) NOT NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`role\` VARCHAR(100) NULL,
  \`email\` VARCHAR(255) NULL,
  \`phone\` VARCHAR(50) NULL,
  \`is_primary\` BOOLEAN NOT NULL DEFAULT FALSE,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`company_id_idx\` (\`company_id\` ASC),
  CONSTRAINT \`fk_company_contacts_company_id\`
    FOREIGN KEY (\`company_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`systems\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`systems\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`description\` TEXT NULL,
  \`category\` ENUM('erp', 'crm', 'ecommerce', 'financeiro', 'rh', 'custom', 'other') NOT NULL,
  \`active\` BOOLEAN NOT NULL DEFAULT TRUE,
  \`logo_url\` VARCHAR(255) NULL,
  \`company_id\` VARCHAR(36) NULL COMMENT 'Owner company if it is a proprietary system',
  \`default_sla\` JSON NULL COMMENT 'Default SLA times in hours for each priority',
  \`documentation_url\` VARCHAR(255) NULL,
  \`created_by\` VARCHAR(36) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`company_id_idx\` (\`company_id\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_systems_company_id\`
    FOREIGN KEY (\`company_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_systems_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`system_versions\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`system_versions\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`system_id\` VARCHAR(36) NOT NULL,
  \`version\` VARCHAR(50) NOT NULL,
  \`release_date\` DATE NOT NULL,
  \`status\` ENUM('alpha', 'beta', 'release_candidate', 'stable', 'deprecated', 'discontinued') NOT NULL DEFAULT 'stable',
  \`changelog\` TEXT NULL,
  \`supported_until\` DATE NULL,
  \`is_lts\` BOOLEAN NOT NULL DEFAULT FALSE,
  \`created_by\` VARCHAR(36) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`system_id_idx\` (\`system_id\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_system_versions_system_id\`
    FOREIGN KEY (\`system_id\`)
    REFERENCES \`systems\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_system_versions_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`company_systems\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`company_systems\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`company_id\` VARCHAR(36) NOT NULL,
  \`system_id\` VARCHAR(36) NOT NULL,
  \`version_id\` VARCHAR(36) NULL,
  \`installation_date\` DATE NOT NULL,
  \`status\` ENUM('active', 'inactive', 'maintenance', 'migration', 'development') NOT NULL DEFAULT 'active',
  \`license_info\` JSON NULL COMMENT 'License type, expiry, users, key',
  \`access_info\` JSON NULL COMMENT 'URL, host, database, username, etc.',
  \`custom_settings\` JSON NULL,
  \`maintenance_history\` JSON NULL,
  \`created_by\` VARCHAR(36) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  UNIQUE INDEX \`company_system_UNIQUE\` (\`company_id\` ASC, \`system_id\` ASC),
  INDEX \`system_id_idx\` (\`system_id\` ASC),
  INDEX \`version_id_idx\` (\`version_id\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_company_systems_company_id\`
    FOREIGN KEY (\`company_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_company_systems_system_id\`
    FOREIGN KEY (\`system_id\`)
    REFERENCES \`systems\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_company_systems_version_id\`
    FOREIGN KEY (\`version_id\`)
    REFERENCES \`system_versions\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_company_systems_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`remote_access\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`remote_access\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`company_id\` VARCHAR(36) NOT NULL,
  \`type\` ENUM('teamviewer', 'anydesk', 'rdp', 'vpn', 'ssh', 'other') NOT NULL,
  \`identifier\` VARCHAR(255) NOT NULL,
  \`password\` VARCHAR(255) NULL,
  \`notes\` TEXT NULL,
  \`created_by\` VARCHAR(36) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`company_id_idx\` (\`company_id\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_remote_access_company_id\`
    FOREIGN KEY (\`company_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_remote_access_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`tickets\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`tickets\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`title\` VARCHAR(255) NOT NULL,
  \`description\` TEXT NOT NULL,
  \`type\` ENUM('support', 'development', 'project') NOT NULL,
  \`status\` ENUM('open', 'analysis', 'in_progress', 'resolved', 'closed', 'reopened') NOT NULL DEFAULT 'open',
  \`priority\` ENUM('low', 'medium', 'high', 'critical') NOT NULL DEFAULT 'medium',
  \`category\` VARCHAR(100) NULL,
  \`subcategory\` VARCHAR(100) NULL,
  \`company_id\` VARCHAR(36) NOT NULL,
  \`system_id\` VARCHAR(36) NULL,
  \`version_id\` VARCHAR(36) NULL,
  \`requester_id\` VARCHAR(36) NOT NULL,
  \`assigned_to\` VARCHAR(36) NULL,
  \`due_date\` DATETIME NULL,
  \`sla_breach\` BOOLEAN NOT NULL DEFAULT FALSE,
  \`resolution_notes\` TEXT NULL,
  \`resolution_date\` DATETIME NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`company_id_idx\` (\`company_id\` ASC),
  INDEX \`system_id_idx\` (\`system_id\` ASC),
  INDEX \`version_id_idx\` (\`version_id\` ASC),
  INDEX \`requester_id_idx\` (\`requester_id\` ASC),
  INDEX \`assigned_to_idx\` (\`assigned_to\` ASC),
  INDEX \`status_idx\` (\`status\` ASC),
  INDEX \`priority_idx\` (\`priority\` ASC),
  CONSTRAINT \`fk_tickets_company_id\`
    FOREIGN KEY (\`company_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_tickets_system_id\`
    FOREIGN KEY (\`system_id\`)
    REFERENCES \`systems\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_tickets_version_id\`
    FOREIGN KEY (\`version_id\`)
    REFERENCES \`system_versions\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_tickets_requester_id\`
    FOREIGN KEY (\`requester_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_tickets_assigned_to\`
    FOREIGN KEY (\`assigned_to\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`ticket_comments\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`ticket_comments\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`ticket_id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`content\` TEXT NOT NULL,
  \`type\` ENUM('comment', 'status_change', 'assignment', 'resolution') NOT NULL DEFAULT 'comment',
  \`old_value\` VARCHAR(255) NULL,
  \`new_value\` VARCHAR(255) NULL,
  \`is_private\` BOOLEAN NOT NULL DEFAULT FALSE,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`ticket_id_idx\` (\`ticket_id\` ASC),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_ticket_comments_ticket_id\`
    FOREIGN KEY (\`ticket_id\`)
    REFERENCES \`tickets\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_ticket_comments_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`attachments\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`attachments\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`url\` VARCHAR(255) NOT NULL,
  \`size\` INT NOT NULL,
  \`type\` VARCHAR(100) NOT NULL,
  \`entity_type\` ENUM('ticket', 'comment', 'knowledge', 'project', 'message') NOT NULL,
  \`entity_id\` VARCHAR(36) NOT NULL,
  \`uploaded_by\` VARCHAR(36) NOT NULL,
  \`uploaded_at\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`entity_id_idx\` (\`entity_id\` ASC),
  INDEX \`uploaded_by_idx\` (\`uploaded_by\` ASC),
  CONSTRAINT \`fk_attachments_uploaded_by\`
    FOREIGN KEY (\`uploaded_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`knowledge_base\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`knowledge_base\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`title\` VARCHAR(255) NOT NULL,
  \`content\` TEXT NOT NULL,
  \`category\` VARCHAR(100) NOT NULL,
  \`visibility\` ENUM('public', 'internal', 'client_specific') NOT NULL DEFAULT 'internal',
  \`company_id\` VARCHAR(36) NULL COMMENT 'If visibility is client_specific',
  \`status\` ENUM('draft', 'published', 'archived') NOT NULL DEFAULT 'draft',
  \`author_id\` VARCHAR(36) NOT NULL,
  \`reviewer_id\` VARCHAR(36) NULL,
  \`publish_date\` DATETIME NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`category_idx\` (\`category\` ASC),
  INDEX \`company_id_idx\` (\`company_id\` ASC),
  INDEX \`author_id_idx\` (\`author_id\` ASC),
  INDEX \`reviewer_id_idx\` (\`reviewer_id\` ASC),
  CONSTRAINT \`fk_knowledge_base_company_id\`
    FOREIGN KEY (\`company_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_knowledge_base_author_id\`
    FOREIGN KEY (\`author_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_knowledge_base_reviewer_id\`
    FOREIGN KEY (\`reviewer_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`knowledge_tags\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`knowledge_tags\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`name\` VARCHAR(100) NOT NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  UNIQUE INDEX \`name_UNIQUE\` (\`name\` ASC)
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`knowledge_article_tags\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`knowledge_article_tags\` (
  \`article_id\` VARCHAR(36) NOT NULL,
  \`tag_id\` VARCHAR(36) NOT NULL,
  PRIMARY KEY (\`article_id\`, \`tag_id\`),
  INDEX \`tag_id_idx\` (\`tag_id\` ASC),
  CONSTRAINT \`fk_knowledge_article_tags_article_id\`
    FOREIGN KEY (\`article_id\`)
    REFERENCES \`knowledge_base\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_knowledge_article_tags_tag_id\`
    FOREIGN KEY (\`tag_id\`)
    REFERENCES \`knowledge_tags\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`knowledge_related_tickets\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`knowledge_related_tickets\` (
  \`article_id\` VARCHAR(36) NOT NULL,
  \`ticket_id\` VARCHAR(36) NOT NULL,
  PRIMARY KEY (\`article_id\`, \`ticket_id\`),
  INDEX \`ticket_id_idx\` (\`ticket_id\` ASC),
  CONSTRAINT \`fk_knowledge_related_tickets_article_id\`
    FOREIGN KEY (\`article_id\`)
    REFERENCES \`knowledge_base\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_knowledge_related_tickets_ticket_id\`
    FOREIGN KEY (\`ticket_id\`)
    REFERENCES \`tickets\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`projects\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`projects\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`description\` TEXT NULL,
  \`client_id\` VARCHAR(36) NOT NULL,
  \`status\` ENUM('planning', 'in_progress', 'paused', 'completed') NOT NULL DEFAULT 'planning',
  \`start_date\` DATE NOT NULL,
  \`end_date\` DATE NULL,
  \`budget\` DECIMAL(10,2) NULL,
  \`budget_spent\` DECIMAL(10,2) NULL,
  \`estimated_hours\` INT NULL,
  \`hours_spent\` INT NULL,
  \`manager_id\` VARCHAR(36) NULL,
  \`created_by\` VARCHAR(36) NOT NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`client_id_idx\` (\`client_id\` ASC),
  INDEX \`manager_id_idx\` (\`manager_id\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_projects_client_id\`
    FOREIGN KEY (\`client_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_projects_manager_id\`
    FOREIGN KEY (\`manager_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_projects_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`project_members\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`project_members\` (
  \`project_id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`role\` VARCHAR(100) NULL,
  \`added_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`project_id\`, \`user_id\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_project_members_project_id\`
    FOREIGN KEY (\`project_id\`)
    REFERENCES \`projects\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_project_members_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`project_tasks\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`project_tasks\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`project_id\` VARCHAR(36) NOT NULL,
  \`title\` VARCHAR(255) NOT NULL,
  \`description\` TEXT NULL,
  \`status\` ENUM('todo', 'in_progress', 'review', 'done') NOT NULL DEFAULT 'todo',
  \`assigned_to\` VARCHAR(36) NULL,
  \`start_date\` DATE NULL,
  \`due_date\` DATE NULL,
  \`estimated_hours\` INT NULL,
  \`actual_hours\` INT NULL,
  \`order\` INT NOT NULL DEFAULT 0,
  \`created_by\` VARCHAR(36) NOT NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`project_id_idx\` (\`project_id\` ASC),
  INDEX \`assigned_to_idx\` (\`assigned_to\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_project_tasks_project_id\`
    FOREIGN KEY (\`project_id\`)
    REFERENCES \`projects\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_project_tasks_assigned_to\`
    FOREIGN KEY (\`assigned_to\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_project_tasks_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`project_task_dependencies\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`project_task_dependencies\` (
  \`task_id\` VARCHAR(36) NOT NULL,
  \`dependency_id\` VARCHAR(36) NOT NULL,
  PRIMARY KEY (\`task_id\`, \`dependency_id\`),
  INDEX \`dependency_id_idx\` (\`dependency_id\` ASC),
  CONSTRAINT \`fk_project_task_dependencies_task_id\`
    FOREIGN KEY (\`task_id\`)
    REFERENCES \`project_tasks\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_project_task_dependencies_dependency_id\`
    FOREIGN KEY (\`dependency_id\`)
    REFERENCES \`project_tasks\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`invoices\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`invoices\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`company_id\` VARCHAR(36) NOT NULL,
  \`number\` VARCHAR(100) NOT NULL,
  \`amount\` DECIMAL(10,2) NOT NULL,
  \`issue_date\` DATE NOT NULL,
  \`due_date\` DATE NOT NULL,
  \`status\` ENUM('pending', 'paid', 'overdue', 'cancelled') NOT NULL DEFAULT 'pending',
  \`payment_method\` ENUM('pix', 'boleto', 'credit_card', 'transfer') NULL,
  \`payment_details\` JSON NULL,
  \`notes\` TEXT NULL,
  \`payment_date\` DATETIME NULL,
  \`created_by\` VARCHAR(36) NOT NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`company_id_idx\` (\`company_id\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_invoices_company_id\`
    FOREIGN KEY (\`company_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_invoices_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`invoice_items\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`invoice_items\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`invoice_id\` VARCHAR(36) NOT NULL,
  \`description\` VARCHAR(255) NOT NULL,
  \`amount\` DECIMAL(10,2) NOT NULL,
  \`quantity\` DECIMAL(10,2) NOT NULL DEFAULT 1,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`invoice_id_idx\` (\`invoice_id\` ASC),
  CONSTRAINT \`fk_invoice_items_invoice_id\`
    FOREIGN KEY (\`invoice_id\`)
    REFERENCES \`invoices\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`payments\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`payments\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`invoice_id\` VARCHAR(36) NOT NULL,
  \`company_id\` VARCHAR(36) NOT NULL,
  \`amount\` DECIMAL(10,2) NOT NULL,
  \`payment_date\` DATETIME NOT NULL,
  \`payment_method\` ENUM('pix', 'boleto', 'credit_card', 'transfer') NOT NULL,
  \`status\` ENUM('pending', 'processing', 'confirmed', 'failed', 'refunded') NOT NULL,
  \`transaction_id\` VARCHAR(255) NULL,
  \`proof_url\` VARCHAR(255) NULL,
  \`notes\` TEXT NULL,
  \`created_by\` VARCHAR(36) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`invoice_id_idx\` (\`invoice_id\` ASC),
  INDEX \`company_id_idx\` (\`company_id\` ASC),
  INDEX \`created_by_idx\` (\`created_by\` ASC),
  CONSTRAINT \`fk_payments_invoice_id\`
    FOREIGN KEY (\`invoice_id\`)
    REFERENCES \`invoices\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_payments_company_id\`
    FOREIGN KEY (\`company_id\`)
    REFERENCES \`companies\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_payments_created_by\`
    FOREIGN KEY (\`created_by\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`chat_messages\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`chat_messages\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`sender_id\` VARCHAR(36) NOT NULL,
  \`ticket_id\` VARCHAR(36) NULL,
  \`receiver_id\` VARCHAR(36) NULL,
  \`department\` ENUM('support', 'development') NULL,
  \`content\` TEXT NOT NULL,
  \`type\` ENUM('text', 'file', 'audio', 'system') NOT NULL DEFAULT 'text',
  \`is_private\` BOOLEAN NOT NULL DEFAULT FALSE,
  \`is_official\` BOOLEAN NOT NULL DEFAULT FALSE,
  \`reply_to\` VARCHAR(36) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`sender_id_idx\` (\`sender_id\` ASC),
  INDEX \`ticket_id_idx\` (\`ticket_id\` ASC),
  INDEX \`receiver_id_idx\` (\`receiver_id\` ASC),
  INDEX \`reply_to_idx\` (\`reply_to\` ASC),
  CONSTRAINT \`fk_chat_messages_sender_id\`
    FOREIGN KEY (\`sender_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_chat_messages_ticket_id\`
    FOREIGN KEY (\`ticket_id\`)
    REFERENCES \`tickets\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_chat_messages_receiver_id\`
    FOREIGN KEY (\`receiver_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_chat_messages_reply_to\`
    FOREIGN KEY (\`reply_to\`)
    REFERENCES \`chat_messages\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`message_read_status\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`message_read_status\` (
  \`message_id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`read_at\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`message_id\`, \`user_id\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_message_read_status_message_id\`
    FOREIGN KEY (\`message_id\`)
    REFERENCES \`chat_messages\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_message_read_status_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`message_reactions\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`message_reactions\` (
  \`message_id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`emoji\` VARCHAR(50) NOT NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`message_id\`, \`user_id\`, \`emoji\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_message_reactions_message_id\`
    FOREIGN KEY (\`message_id\`)
    REFERENCES \`chat_messages\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_message_reactions_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`message_mentions\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`message_mentions\` (
  \`message_id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`message_id\`, \`user_id\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_message_mentions_message_id\`
    FOREIGN KEY (\`message_id\`)
    REFERENCES \`chat_messages\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_message_mentions_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`user_notifications\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`user_notifications\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`title\` VARCHAR(255) NOT NULL,
  \`description\` TEXT NOT NULL,
  \`type\` ENUM('ticket', 'message', 'project', 'system', 'invoice', 'user', 'general') NOT NULL,
  \`related_id\` VARCHAR(36) NULL,
  \`action_url\` VARCHAR(255) NULL,
  \`read\` BOOLEAN NOT NULL DEFAULT FALSE,
  \`priority\` ENUM('low', 'medium', 'high') NOT NULL DEFAULT 'medium',
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_user_notifications_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`user_favorites\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`user_favorites\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`item_type\` ENUM('ticket', 'client', 'project', 'knowledge', 'system') NOT NULL,
  \`item_id\` VARCHAR(36) NOT NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`description\` TEXT NULL,
  \`icon\` VARCHAR(50) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  UNIQUE INDEX \`user_item_UNIQUE\` (\`user_id\` ASC, \`item_type\` ASC, \`item_id\` ASC),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_user_favorites_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`user_calendar_events\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`user_calendar_events\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`title\` VARCHAR(255) NOT NULL,
  \`description\` TEXT NULL,
  \`start_date\` DATETIME NOT NULL,
  \`end_date\` DATETIME NOT NULL,
  \`all_day\` BOOLEAN NOT NULL DEFAULT FALSE,
  \`color\` VARCHAR(50) NULL,
  \`related_type\` ENUM('ticket', 'project', 'meeting', 'internal', 'external', 'other') NULL,
  \`related_id\` VARCHAR(36) NULL,
  \`status\` ENUM('scheduled', 'in_progress', 'completed', 'cancelled') NOT NULL DEFAULT 'scheduled',
  \`notification\` BOOLEAN NOT NULL DEFAULT TRUE,
  \`notification_time\` INT NOT NULL DEFAULT 15,
  \`location\` VARCHAR(255) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_user_calendar_events_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`event_participants\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`event_participants\` (
  \`event_id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`status\` ENUM('invited', 'accepted', 'declined', 'tentative') NOT NULL DEFAULT 'invited',
  \`response_date\` DATETIME NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`event_id\`, \`user_id\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  CONSTRAINT \`fk_event_participants_event_id\`
    FOREIGN KEY (\`event_id\`)
    REFERENCES \`user_calendar_events\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_event_participants_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`timesheet_entries\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`timesheet_entries\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`date\` DATE NOT NULL,
  \`start_time\` TIME NULL,
  \`end_time\` TIME NULL,
  \`duration\` INT NOT NULL COMMENT 'Duration in minutes',
  \`activity_type\` ENUM('ticket', 'project', 'internal', 'meeting', 'training', 'admin', 'other') NOT NULL,
  \`related_id\` VARCHAR(36) NULL,
  \`description\` TEXT NOT NULL,
  \`billable\` BOOLEAN NOT NULL DEFAULT TRUE,
  \`status\` ENUM('draft', 'submitted', 'approved', 'rejected') NOT NULL DEFAULT 'draft',
  \`reviewer_id\` VARCHAR(36) NULL,
  \`review_date\` DATETIME NULL,
  \`review_notes\` TEXT NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  \`updated_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  INDEX \`reviewer_id_idx\` (\`reviewer_id\` ASC),
  CONSTRAINT \`fk_timesheet_entries_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE,
  CONSTRAINT \`fk_timesheet_entries_reviewer_id\`
    FOREIGN KEY (\`reviewer_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE SET NULL
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Table \`audit_logs\`
-- -----------------------------------------------------
CREATE TABLE IF NOT EXISTS \`audit_logs\` (
  \`id\` VARCHAR(36) NOT NULL,
  \`user_id\` VARCHAR(36) NOT NULL,
  \`action\` ENUM('create', 'update', 'delete', 'view', 'payment_process', 'status_change', 'assignment_change') NOT NULL,
  \`entity_type\` ENUM('ticket', 'invoice', 'payment', 'company', 'user', 'system', 'project') NOT NULL,
  \`entity_id\` VARCHAR(36) NOT NULL,
  \`old_value\` JSON NULL,
  \`new_value\` JSON NULL,
  \`ip_address\` VARCHAR(50) NULL,
  \`user_agent\` VARCHAR(255) NULL,
  \`created_date\` DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`),
  INDEX \`user_id_idx\` (\`user_id\` ASC),
  INDEX \`entity_type_idx\` (\`entity_type\` ASC),
  INDEX \`entity_id_idx\` (\`entity_id\` ASC),
  CONSTRAINT \`fk_audit_logs_user_id\`
    FOREIGN KEY (\`user_id\`)
    REFERENCES \`users\` (\`id\`)
    ON DELETE CASCADE
    ON UPDATE CASCADE
) ENGINE = InnoDB DEFAULT CHARACTER SET = utf8mb4 COLLATE = utf8mb4_unicode_ci;

-- -----------------------------------------------------
-- Stored Procedures
-- -----------------------------------------------------

-- Update ticket status and track history
DELIMITER $$
CREATE PROCEDURE \`update_ticket_status\`(
  IN ticket_id VARCHAR(36),
  IN new_status ENUM('open', 'analysis', 'in_progress', 'resolved', 'closed', 'reopened'),
  IN user_id VARCHAR(36),
  IN comment_text TEXT
)
BEGIN
  DECLARE old_status VARCHAR(50);
  
  -- Get current status
  SELECT status INTO old_status FROM tickets WHERE id = ticket_id;
  
  -- Update ticket status
  UPDATE tickets SET 
    status = new_status,
    updated_date = NOW()
  WHERE id = ticket_id;
  
  -- Add comment with status change
  INSERT INTO ticket_comments (
    id, 
    ticket_id, 
    user_id, 
    content, 
    type, 
    old_value, 
    new_value
  ) VALUES (
    UUID(), 
    ticket_id, 
    user_id, 
    IFNULL(comment_text, CONCAT('Status alterado de ', old_status, ' para ', new_status)),
    'status_change',
    old_status,
    new_status
  );
  
  -- Create audit log
  INSERT INTO audit_logs (
    id,
    user_id,
    action,
    entity_type,
    entity_id,
    old_value,
    new_value
  ) VALUES (
    UUID(),
    user_id,
    'status_change',
    'ticket',
    ticket_id,
    JSON_OBJECT('status', old_status),
    JSON_OBJECT('status', new_status)
  );
END$$
DELIMITER ;

-- Assign ticket to user and track history
DELIMITER $$
CREATE PROCEDURE \`assign_ticket\`(
  IN ticket_id VARCHAR(36),
  IN assigned_user_id VARCHAR(36),
  IN assigner_user_id VARCHAR(36),
  IN comment_text TEXT
)
BEGIN
  DECLARE old_assigned_user VARCHAR(36);
  
  -- Get current assigned user
  SELECT assigned_to INTO old_assigned_user FROM tickets WHERE id = ticket_id;
  
  -- Update ticket assignment
  UPDATE tickets SET 
    assigned_to = assigned_user_id,
    updated_date = NOW()
  WHERE id = ticket_id;
  
  -- Add comment with assignment change
  INSERT INTO ticket_comments (
    id, 
    ticket_id, 
    user_id, 
    content, 
    type, 
    old_value, 
    new_value
  ) VALUES (
    UUID(), 
    ticket_id, 
    assigner_user_id, 
    IFNULL(comment_text, 'Atribuição de chamado alterada'),
    'assignment',
    old_assigned_user,
    assigned_user_id
  );
  
  -- Create audit log
  INSERT INTO audit_logs (
    id,
    user_id,
    action,
    entity_type,
    entity_id,
    old_value,
    new_value
  ) VALUES (
    UUID(),
    assigner_user_id,
    'assignment_change',
    'ticket',
    ticket_id,
    JSON_OBJECT('assigned_to', old_assigned_user),
    JSON_OBJECT('assigned_to', assigned_user_id)
  );
  
  -- Create notification for assigned user
  IF assigned_user_id IS NOT NULL THEN
    INSERT INTO user_notifications (
      id,
      user_id,
      title,
      description,
      type,
      related_id,
      action_url,
      priority
    )
    SELECT
      UUID(),
      assigned_user_id,
      CONCAT('Chamado atribuído a você: #', SUBSTRING(ticket_id, 1, 8)),
      CONCAT('O chamado "', title, '" foi atribuído a você'),
      'ticket',
      ticket_id,
      CONCAT('/tickets/', ticket_id),
      CASE
        WHEN priority = 'critical' THEN 'high'
        WHEN priority = 'high' THEN 'high'
        WHEN priority = 'medium' THEN 'medium'
        ELSE 'low'
      END
    FROM tickets
    WHERE id = ticket_id;
  END IF;
END$$
DELIMITER ;

-- Process payment and update invoice
DELIMITER $$
CREATE PROCEDURE \`process_payment\`(
  IN invoice_id VARCHAR(36),
  IN payment_method ENUM('pix', 'boleto', 'credit_card', 'transfer'),
  IN payment_amount DECIMAL(10,2),
  IN transaction_id VARCHAR(255),
  IN proof_url VARCHAR(255),
  IN notes TEXT,
  IN processor_user_id VARCHAR(36)
)
BEGIN
  DECLARE invoice_total DECIMAL(10,2);
  DECLARE company_id_val VARCHAR(36);
  DECLARE payment_id VARCHAR(36);
  
  -- Get invoice data
  SELECT amount, company_id INTO invoice_total, company_id_val 
  FROM invoices WHERE id = invoice_id;
  
  -- Generate payment ID
  SET payment_id = UUID();
  
  -- Create payment record
  INSERT INTO payments (
    id,
    invoice_id,
    company_id,
    amount,
    payment_date,
    payment_method,
    status,
    transaction_id,
    proof_url,
    notes,
    created_by
  ) VALUES (
    payment_id,
    invoice_id,
    company_id_val,
    payment_amount,
    NOW(),
    payment_method,
    'confirmed',
    transaction_id,
    proof_url,
    notes,
    processor_user_id
  );
  
  -- Update invoice if payment completes it
  IF payment_amount >= invoice_total THEN
    UPDATE invoices SET
      status = 'paid',
      payment_date = NOW(),
      payment_method = payment_method,
      updated_date = NOW()
    WHERE id = invoice_id;
  END IF;
  
  -- Create audit log
  INSERT INTO audit_logs (
    id,
    user_id,
    action,
    entity_type,
    entity_id,
    new_value
  ) VALUES (
    UUID(),
    processor_user_id,
    'payment_process',
    'payment',
    payment_id,
    JSON_OBJECT(
      'amount', payment_amount,
      'method', payment_method,
      'status', 'confirmed',
      'invoice_id', invoice_id
    )
  );
  
  -- Create notification for company users
  INSERT INTO user_notifications (
    id,
    user_id,
    title,
    description,
    type,
    related_id,
    action_url,
    priority
  )
  SELECT
    UUID(),
    u.id,
    CONCAT('Pagamento processado: Fatura #', invoices.number),
    CONCAT('Um pagamento de R$ ', payment_amount, ' foi processado para a fatura #', invoices.number),
    'invoice',
    invoice_id,
    CONCAT('/financial/invoices/', invoice_id),
    'medium'
  FROM users u
  JOIN invoices ON invoices.id = invoice_id
  WHERE u.company_id = company_id_val AND u.role = 'client';
END$$
DELIMITER ;

-- Check for overdue invoices and update status
DELIMITER $$
CREATE EVENT \`check_overdue_invoices\`
ON SCHEDULE EVERY 1 DAY STARTS CURRENT_DATE
DO
BEGIN
  -- Update invoice status to overdue
  UPDATE invoices
  SET status = 'overdue'
  WHERE status = 'pending' AND due_date < CURRENT_DATE;
  
  -- Create notifications for overdue invoices
  INSERT INTO user_notifications (
    id,
    user_id,
    title,
    description,
    type,
    related_id,
    action_url,
    priority
  )
  SELECT
    UUID(),
    u.id,
    CONCAT('Fatura vencida: #', i.number),
    CONCAT('A fatura #', i.number, ' no valor de R$ ', i.amount, ' está vencida desde ', DATE_FORMAT(i.due_date, '%d/%m/%Y')),
    'invoice',
    i.id,
    CONCAT('/financial/invoices/', i.id),
    'high'
  FROM invoices i
  JOIN users u ON u.company_id = i.company_id
  WHERE i.status = 'overdue' 
    AND i.due_date = DATE_SUB(CURRENT_DATE, INTERVAL 1 DAY)
    AND u.role = 'client';
END$$
DELIMITER ;

-- Check for SLA breaches
DELIMITER $$
CREATE EVENT \`check_sla_breaches\`
ON SCHEDULE EVERY 1 HOUR STARTS CURRENT_TIMESTAMP
DO
BEGIN
  -- Update tickets with breached SLA
  UPDATE tickets t
  JOIN companies c ON t.company_id = c.id
  SET 
    t.sla_breach = TRUE,
    t.updated_date = NOW()
  WHERE 
    t.status IN ('open', 'analysis', 'in_progress') 
    AND t.sla_breach = FALSE
    AND t.due_date < NOW();
  
  -- Create notifications for SLA breaches
  INSERT INTO user_notifications (
    id,
    user_id,
    title,
    description,
    type,
    related_id,
    action_url,
    priority
  )
  SELECT
    UUID(),
    t.assigned_to,
    CONCAT('SLA violado: Chamado #', SUBSTRING(t.id, 1, 8)),
    CONCAT('O SLA do chamado "', t.title, '" foi violado'),
    'ticket',
    t.id,
    CONCAT('/tickets/', t.id),
    'high'
  FROM tickets t
  WHERE 
    t.status IN ('open', 'analysis', 'in_progress')
    AND t.sla_breach = TRUE
    AND t.assigned_to IS NOT NULL
    AND t.updated_date > DATE_SUB(NOW(), INTERVAL 1 HOUR);
END$$
DELIMITER ;

SET FOREIGN_KEY_CHECKS = 1;`;

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <CardTitle className="flex items-center gap-2">
          <Database className="h-6 w-6" />
          Esquema do Banco de Dados MySQL
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Tabs defaultValue="sql">
          <TabsList className="w-full rounded-none border-b">
            <TabsTrigger value="sql" className="flex-1">Script SQL</TabsTrigger>
            <TabsTrigger value="info" className="flex-1">Informações</TabsTrigger>
          </TabsList>
          
          <TabsContent value="sql" className="p-0">
            <div className="relative">
              <div className="absolute right-4 top-4 flex gap-2 z-10">
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white/80 backdrop-blur-sm"
                  onClick={handleCopyToClipboard}
                >
                  {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copied ? 'Copiado!' : 'Copiar'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white/80 backdrop-blur-sm"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
              </div>
              
              <ScrollArea className="h-[500px] border-0">
                <pre className="bg-slate-950 p-4 rounded-md text-xs md:text-sm text-slate-50 overflow-auto">
                  <code>{sqlScript}</code>
                </pre>
              </ScrollArea>
            </div>
          </TabsContent>
          
          <TabsContent value="info" className="p-6">
            <h3 className="text-lg font-semibold mb-4">Estrutura do Banco de Dados</h3>
            <p className="text-muted-foreground mb-4">
              Este script SQL cria um banco de dados completo para o sistema HelpDesk Pro, incluindo:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li><span className="font-medium">19 tabelas principais</span> cobrindo todas as entidades do sistema</li>
              <li><span className="font-medium">Procedimentos armazenados</span> para gerenciamento de chamados e pagamentos</li>
              <li><span className="font-medium">Eventos automáticos</span> para controle de SLA e faturas vencidas</li>
              <li><span className="font-medium">Relacionamentos completos</span> entre todas as entidades</li>
              <li><span className="font-medium">Suporte a JSON</span> para campos complexos e configurações</li>
            </ul>
            
            <h3 className="text-lg font-semibold mb-4">Instruções de Uso</h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Copie o script completo usando o botão "Copiar"</li>
              <li>Cole o script em seu cliente MySQL (como MySQL Workbench, phpMyAdmin, etc.)</li>
              <li>Execute o script para criar o banco de dados e todas as tabelas</li>
              <li>Recomendamos usar MySQL 8.0 ou superior para suporte completo a todas as funcionalidades</li>
            </ol>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}